/**
 * Deepfake replies: the web's copy of the API's reply-inbox, thread, agent and outcome contract.
 *
 * HumanFirewall's Deepfake Simulation is the authorised, consented awareness exercise an organisation
 * runs against its OWN employees. When an employee answers the simulation e-mail the admin sees the
 * reply in the product and answers by hand, accepts a drafted answer, or lets an agent answer inside
 * limits; a person who shares what the exercise asked for is marked compromised and trained. These are
 * the shapes of those endpoints (apps/api/src/modules/deepfake/{replies,agent,outcome}/...), mirrored
 * name for name.
 *
 * `lib/deepfake/__tests__/replies-schemas.guard.spec.ts` reads those API files and fails when a list or
 * a field name here drifts from them, and when a member has no sentence in one of the four catalogues.
 * A declared union one member short of the API's enum is invisible to tsc and a crash the day the API
 * sends the member (CLAUDE.md, "the sixth payload type"); so a display key is chosen through a function
 * that never returns undefined (`deepfakeIntentKey`, `deepfakeKindKey`, `deepfakeEventKey`, ...), and a
 * refusal is worded from its `errorCode` (`deepfakeReplyErrorKey`), never from the API's English message.
 *
 * NO FIELD HERE CARRIES AN EMPLOYEE'S REPLY BODY, and none may: the API serves a redacted, truncated
 * preview (`redactedPreview`) and masked KINDS, nothing else. The only body-named field is the agent's
 * OWN draft (`DeepfakeDraft.bodyText`), which is our text. The response schemas strip every key they do
 * not name, so even a field that leaked onto the wire would never reach a component.
 */
import { z } from 'zod'

// ── vocabularies (parsed against the API source by the guard spec) ───────────

/** The inbox filters (`DEEPFAKE_REPLY_FILTERS`). The screen shows five chips; `needs-review` is a row badge and a link. */
export const DEEPFAKE_REPLY_FILTERS = ['needs-reply', 'needs-review', 'compromised', 'verified', 'automatic'] as const
export type DeepfakeReplyFilter = (typeof DEEPFAKE_REPLY_FILTERS)[number]

/** Classification intents that mean the person resisted: they verified out of band or reported it. */
export const DEEPFAKE_VERIFIED_INTENTS = ['verifies_out_of_band', 'reports'] as const
/** Thread stop reasons that mean the same thing. */
export const DEEPFAKE_VERIFIED_STOP_REASONS = ['reported', 'callback'] as const

/** Every `deepfake_thread_status` member a response can carry. */
export const DEEPFAKE_THREAD_STATUSES = ['Open', 'AwaitingAgent', 'AgentDrafted', 'AwaitingApproval', 'Replied', 'Stopped', 'Closed'] as const
export type DeepfakeThreadStatus = (typeof DEEPFAKE_THREAD_STATUSES)[number]

/** Every `deepfake_reply_mode` member: who answers a thread. */
export const DEEPFAKE_REPLY_MODES = ['auto', 'assist', 'me'] as const
export type DeepfakeReplyMode = (typeof DEEPFAKE_REPLY_MODES)[number]

/** Every `deepfake_message_role` member. */
export const DEEPFAKE_MESSAGE_ROLES = ['recipient', 'agent', 'admin', 'system'] as const
export type DeepfakeMessageRole = (typeof DEEPFAKE_MESSAGE_ROLES)[number]

/** Every `deepfake_draft_status` member. */
export const DEEPFAKE_DRAFT_STATUSES = ['Pending', 'Approved', 'Sent', 'Discarded', 'Expired'] as const
export type DeepfakeDraftStatus = (typeof DEEPFAKE_DRAFT_STATUSES)[number]

/** The kinds of information a simulation may ask for and a reply may carry (`deepfake_ask_kind`). */
export const DEEPFAKE_ASK_KINDS = ['approval_code', 'employee_id', 'manager_name', 'document', 'verbal_yes', 'other'] as const
export type DeepfakeAskKind = (typeof DEEPFAKE_ASK_KINDS)[number]

/** What a reply is for, as the classifier reads it. */
export const DEEPFAKE_REPLY_INTENTS = ['asks_more_info', 'provides_info', 'verifies_out_of_band', 'reports', 'other'] as const
export type DeepfakeReplyIntent = (typeof DEEPFAKE_REPLY_INTENTS)[number]

/** Shapes the redactor masked on arrival and never kept. */
export const DEEPFAKE_SENSITIVE_SHAPES = ['card', 'iban', 'password', 'long_digits'] as const
export type DeepfakeSensitiveShape = (typeof DEEPFAKE_SENSITIVE_SHAPES)[number]

/** Every `deepfake_event_type` member (the timeline of a person). */
export const DEEPFAKE_TIMELINE_EVENT_TYPES = [
  'InviteQueued',
  'InviteSent',
  'InviteFailed',
  'EmailOpened',
  'LinkClicked',
  'MeetingViewed',
  'Joined',
  'VideoStarted',
  'VideoProgress',
  'VideoWatched',
  'MeetingLeft',
  'FollowupSent',
  'ReplyReceived',
  'AgentDrafted',
  'AgentReplied',
  'AdminReplied',
  'Reported',
  'Compromised',
  'TrainingEnrolled',
  'AutoPaused',
  'ClassificationSkipped',
  'DraftExpired',
] as const
export type DeepfakeTimelineEventType = (typeof DEEPFAKE_TIMELINE_EVENT_TYPES)[number]

/** Why a text may not be sent (the output safety filter's reasons). */
export const DEEPFAKE_UNSAFE_REASONS = [
  'empty',
  'too_long',
  'secret_password',
  'secret_pin',
  'secret_card',
  'secret_bank_account',
  'secret_national_id',
  'secret_one_time_code',
  'secret_security_answer',
  'foreign_link',
] as const
export type DeepfakeUnsafeReason = (typeof DEEPFAKE_UNSAFE_REASONS)[number]

/**
 * `thread.stoppedReason` values: the agent area's five, plus `compromised` (the outcome area) and `killed` (the
 * campaign kill switch). The API types the field as free text, so the screen words an unknown reason too.
 */
export const DEEPFAKE_THREAD_STOP_REASONS = [
  'max_exchanges',
  'window_closed',
  'reported',
  'callback',
  'admin_stopped',
  'compromised',
  'killed',
] as const
export type DeepfakeThreadStopReason = (typeof DEEPFAKE_THREAD_STOP_REASONS)[number]

/** Where the training a compromise owes stands. */
export const DEEPFAKE_TRAINING_STATES = ['enrolled', 'already_enrolled', 'credited', 'skipped', 'pending', 'failed'] as const
export type DeepfakeTrainingState = (typeof DEEPFAKE_TRAINING_STATES)[number]
/** Why training was skipped, or why the last attempt failed. */
export const DEEPFAKE_TRAINING_REASONS = ['recipient_ineligible', 'suppressed', 'course_unavailable', 'error'] as const

/** The longest an admin-written reply may be (the API refuses more). */
export const ADMIN_REPLY_MAX_CHARS = 5000
/** The longest reason an admin may give for confirming or clearing a compromise. */
export const MARK_COMPROMISED_REASON_MAX = 500

/**
 * Every `errorCode` the replies, agent and outcome endpoints answer with. Each has a sentence under
 * `deepfake.replies.errors.<code>`.
 */
export const DEEPFAKE_REPLY_ERROR_CODES = [
  // replies/replies-error-codes.ts
  'deepfake_thread_not_found',
  'deepfake_replies_query_invalid',
  // agent/agent-error-codes.ts
  'deepfake_thread_id_invalid',
  'deepfake_draft_not_found',
  'deepfake_draft_id_invalid',
  'deepfake_agent_body_invalid',
  'deepfake_module_disabled',
  'deepfake_campaign_not_active',
  'deepfake_thread_stopped',
  'deepfake_thread_compromised',
  'deepfake_max_exchanges',
  'deepfake_reply_window_closed',
  'deepfake_no_recipient_address',
  'deepfake_reply_not_sent',
  'deepfake_reply_request_reused',
  'deepfake_unsafe_reply',
  'deepfake_auto_not_allowed',
  'deepfake_draft_not_pending',
  'deepfake_draft_expired',
  'deepfake_no_message_to_answer',
  'deepfake_draft_residency_blocked',
  'deepfake_draft_ai_disabled',
  'deepfake_draft_quota_exceeded',
  'deepfake_draft_no_ai_key',
  'deepfake_draft_failed',
  // outcome/outcome-error-codes.ts
  'deepfake_target_not_found',
] as const
export type DeepfakeReplyErrorCode = (typeof DEEPFAKE_REPLY_ERROR_CODES)[number]

/** The codes the AI allowance guard (ai-metering/quota-exceeded.exception.ts) answers a refused draft with. */
export const DEEPFAKE_AI_QUOTA_ERROR_CODES = ['quota_exhausted_use_own_key', 'daily_ai_limit_reached'] as const

/** The error code that carries the safety filter's `reasons` (a 422). */
export const DEEPFAKE_UNSAFE_REPLY_CODE = 'deepfake_unsafe_reply'
/** The code of an auto mode the company or the campaign does not allow. */
export const DEEPFAKE_AUTO_NOT_ALLOWED_CODE = 'deepfake_auto_not_allowed'

// ── display keys: a member this build does not know is "unknown", never undefined ──

const keyIn = (family: string, members: readonly string[], member: string | null | undefined, fallback = 'unknown'): string =>
  `deepfake.replies.${family}.${member && members.includes(member) ? member : fallback}`

export const deepfakeIntentKey = (intent: string | null | undefined) => keyIn('intent', DEEPFAKE_REPLY_INTENTS, intent)
export const deepfakeKindKey = (kind: string | null | undefined) => keyIn('kinds', DEEPFAKE_ASK_KINDS, kind)
export const deepfakeEventKey = (type: string | null | undefined) => keyIn('events', DEEPFAKE_TIMELINE_EVENT_TYPES, type)
export const deepfakeUnsafeKey = (reason: string | null | undefined) => keyIn('unsafe', DEEPFAKE_UNSAFE_REASONS, reason)
export const deepfakeStopReasonKey = (reason: string | null | undefined) => keyIn('stopReasons', DEEPFAKE_THREAD_STOP_REASONS, reason)
export const deepfakeShapeKey = (shape: string | null | undefined) => keyIn('shapes', DEEPFAKE_SENSITIVE_SHAPES, shape)
export const deepfakeTrainingKey = (state: string | null | undefined) => keyIn('training', DEEPFAKE_TRAINING_STATES, state)
export const deepfakeTrainingReasonKey = (reason: string | null | undefined) => keyIn('trainingReasons', DEEPFAKE_TRAINING_REASONS, reason)

/** The sentence for a refusal, chosen by its stable code. A code nobody knows is the generic sentence. */
export function deepfakeReplyErrorKey(code: string | null | undefined): string {
  const known: readonly string[] = [...DEEPFAKE_REPLY_ERROR_CODES, ...DEEPFAKE_AI_QUOTA_ERROR_CODES]
  return `deepfake.replies.errors.${code && known.includes(code) ? code : 'generic'}`
}

// ── requests ────────────────────────────────────────────────────────────────

/** GET /v1/deepfake/replies. Only what is set travels; the API defaults page 1 and 25 a page. */
export interface DeepfakeRepliesListParams {
  filter?: DeepfakeReplyFilter
  campaignId?: string
  search?: string
  page?: number
  pageSize?: number
}

// ── responses: replies ───────────────────────────────────────────────────────

const isoInstant = z.string()

/** One conversation, as the inbox row and the detail header show it. */
export const DeepfakeReplyThreadItemSchema = z.object({
  id: z.string(),
  campaignId: z.string(),
  campaignName: z.string(),
  targetId: z.string(),
  recipient: z.object({
    id: z.string(),
    /** First and last name; empty when the person has no row any more. */
    name: z.string(),
    email: z.string(),
  }),
  status: z.enum(DEEPFAKE_THREAD_STATUSES),
  replyMode: z.enum(DEEPFAKE_REPLY_MODES),
  /** Agent and admin answers sent on this thread. */
  exchangeCount: z.number().int().min(0),
  compromised: z.boolean(),
  stoppedReason: z.string().nullable(),
  needsReply: z.boolean(),
  /** DERIVED by the API: the latest classification is advisory and the thread is not compromised. */
  needsReview: z.boolean(),
  /**
   * The latest reply did not come from the person who was invited (a forwarded invitation, anyone holding the
   * Reply-To address): the API stores it, never reads it for what it asks, never counts it, and lists it under
   * "needs review". An older API does not send it: read as false.
   */
  senderMismatch: z.boolean().default(false),
  latestIntent: z.string().nullable(),
  messageCount: z.number().int().min(0),
  /** Messages a person wrote (inbound, not an auto-response). */
  replyCount: z.number().int().min(0),
  /** Redacted, truncated; null when the latest message has no preview. */
  lastMessagePreview: z.string().nullable(),
  lastMessageRole: z.enum(DEEPFAKE_MESSAGE_ROLES).nullable(),
  lastMessageAt: isoInstant.nullable(),
  lastRecipientReplyAt: isoInstant.nullable(),
  lastOutboundAt: isoInstant.nullable(),
  createdAt: isoInstant,
  updatedAt: isoInstant,
})
export type DeepfakeReplyThreadItem = z.infer<typeof DeepfakeReplyThreadItemSchema>

export const DeepfakeReplyListResponseSchema = z.object({
  items: z.array(DeepfakeReplyThreadItemSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  totalPages: z.number().int().min(1),
})
export type DeepfakeReplyListResponse = z.infer<typeof DeepfakeReplyListResponseSchema>

/** GET /v1/deepfake/replies/counts: the sidebar badge and the inbox chips. */
export const DeepfakeReplyCountsSchema = z.object({
  total: z.number().int().min(0),
  needsReply: z.number().int().min(0),
  needsReview: z.number().int().min(0),
  compromised: z.number().int().min(0),
  verified: z.number().int().min(0),
  automatic: z.number().int().min(0),
})
export type DeepfakeReplyCounts = z.infer<typeof DeepfakeReplyCountsSchema>

/** One message of a thread. `redactedPreview` is all the screen ever gets of its text. */
export const DeepfakeThreadMessageSchema = z.object({
  id: z.string(),
  role: z.enum(DEEPFAKE_MESSAGE_ROLES),
  direction: z.enum(['inbound', 'outbound']),
  /** The subject with the same masking as the preview. */
  subject: z.string().nullable(),
  redactedPreview: z.string().nullable(),
  /** Out of office, bounce, no-reply: stored, never answered, never counted. */
  isAutoResponse: z.boolean(),
  classificationId: z.string().nullable(),
  /** The admin who sent an outbound admin or approved-draft message. */
  sentById: z.string().nullable(),
  /**
   * When the retention sweep erased this message's text (past the workspace's "Keep replies (days)"), or null while
   * it is stored. The row, its kinds and the counts stay; the subject and the preview are null once it is set, and
   * the screen says so instead of drawing an empty bubble. `default(null)` so that an answer from an API that does
   * not send the field yet reads as "not erased" rather than failing the whole thread.
   */
  contentErasedAt: isoInstant.nullable().default(null),
  createdAt: isoInstant,
})
export type DeepfakeThreadMessage = z.infer<typeof DeepfakeThreadMessageSchema>

/** What the classifier found in the latest classified message: KINDS and confidence, never values. */
export const DeepfakeClassificationSummarySchema = z.object({
  id: z.string(),
  messageId: z.string(),
  intent: z.string(),
  providesRequestedKinds: z.boolean(),
  overallConfidence: z.number(),
  /** True when the confidence is below the campaign's bar: "possible disclosure", the admin decides. */
  advisory: z.boolean(),
  kinds: z.array(z.object({ kind: z.string(), confidence: z.number() })),
  createdAt: isoInstant,
})
export type DeepfakeClassificationSummary = z.infer<typeof DeepfakeClassificationSummarySchema>

/** What the exercise asked for and what the replies were found to carry: kinds only. */
export const DeepfakeAskedAndReceivedSchema = z.object({
  asked: z.array(z.string()),
  received: z.array(z.string()),
})
export type DeepfakeAskedAndReceived = z.infer<typeof DeepfakeAskedAndReceivedSchema>

/** A file a reply carried: presence, name, size and fingerprint. The bytes are never kept. */
export const DeepfakeAttachmentSummarySchema = z.object({
  filename: z.string(),
  size: z.number().int().min(0),
  sha256: z.string(),
  contentType: z.string(),
})
export type DeepfakeAttachmentSummary = z.infer<typeof DeepfakeAttachmentSummarySchema>

/** One entry of the target's event log. `type` is a string so an event this build does not know is worded, not a parse error. */
export const DeepfakeThreadTimelineItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  at: isoInstant,
  /** The message a `ReplyReceived` event is about. */
  messageId: z.string().nullable(),
  attachments: z.array(DeepfakeAttachmentSummarySchema),
  /** Which sensitive shapes the redactor masked in that reply. */
  sensitiveShapes: z.array(z.string()),
})
export type DeepfakeThreadTimelineItem = z.infer<typeof DeepfakeThreadTimelineItemSchema>

// ── responses: the agent ─────────────────────────────────────────────────────

/** The state of a thread after an action, for the screen to redraw from. */
export const DeepfakeThreadStateSchema = z.object({
  id: z.string(),
  status: z.enum(DEEPFAKE_THREAD_STATUSES),
  replyMode: z.enum(DEEPFAKE_REPLY_MODES),
  exchangeCount: z.number().int(),
  /** The most replies this thread may get: the smaller of the campaign's and the company's ceiling. */
  maxExchanges: z.number().int(),
  stoppedReason: z.string().nullable(),
  nextAllowedSendAt: isoInstant.nullable(),
})
export type DeepfakeThreadState = z.infer<typeof DeepfakeThreadStateSchema>

/** One drafted reply. `unsafe` is derived by the API from the text as it stands now. */
export const DeepfakeDraftSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  status: z.enum(DEEPFAKE_DRAFT_STATUSES),
  /** The AGENT'S text (ours), never an employee's. */
  bodyText: z.string(),
  quickReplies: z.array(z.string()),
  classificationId: z.string().nullable(),
  /** A draft that passes this instant without a decision is Expired and is never sent. */
  expiresAt: isoInstant.nullable(),
  /** When the agent will send this draft on its own (auto mode, thread waiting); null otherwise. */
  autoSendAt: isoInstant.nullable(),
  unsafe: z.boolean(),
  unsafeReasons: z.array(z.string()),
  createdAt: isoInstant,
})
export type DeepfakeDraft = z.infer<typeof DeepfakeDraftSchema>

/** POST /v1/deepfake/threads/:id/reply */
export const SendThreadReplyResponseSchema = z.object({
  thread: DeepfakeThreadStateSchema,
  /** True when the admin's text trips the safety filter. It was sent: the admin is accountable. */
  flagged: z.boolean(),
  warnings: z.array(z.string()),
  /** True when this request repeated one that was already sent: the first result is returned and nothing was sent. */
  duplicate: z.boolean(),
})
export type SendThreadReplyResponse = z.infer<typeof SendThreadReplyResponseSchema>

/** POST /v1/deepfake/threads/:id/draft (and the discard response, which has the same shape). */
export const RequestDraftResponseSchema = z.object({
  draft: DeepfakeDraftSchema,
  thread: DeepfakeThreadStateSchema,
})
export type RequestDraftResponse = z.infer<typeof RequestDraftResponseSchema>

/** POST /v1/deepfake/drafts/:id/approve */
export const ApproveDraftResponseSchema = z.object({
  draft: DeepfakeDraftSchema,
  thread: DeepfakeThreadStateSchema,
  /** The final text differed from the draft (the admin edited it). */
  edited: z.boolean(),
})
export type ApproveDraftResponse = z.infer<typeof ApproveDraftResponseSchema>

/** POST /v1/deepfake/threads/:id/stop and PUT /v1/deepfake/threads/:id/mode */
export const ThreadActionResponseSchema = z.object({ thread: DeepfakeThreadStateSchema })
export type ThreadActionResponse = z.infer<typeof ThreadActionResponseSchema>

// ── responses: the detail and the draft ─────────────────────────────────────

/** GET /v1/deepfake/threads/:id. It carries no draft: the draft waiting on a thread is read from its own endpoint. */
export const DeepfakeThreadDetailSchema = z.object({
  thread: DeepfakeReplyThreadItemSchema,
  campaign: z.object({
    id: z.string(),
    name: z.string(),
    askKinds: z.array(z.string()),
  }),
  messages: z.array(DeepfakeThreadMessageSchema),
  latestClassification: DeepfakeClassificationSummarySchema.nullable(),
  askedAndReceived: DeepfakeAskedAndReceivedSchema,
  timeline: z.array(DeepfakeThreadTimelineItemSchema),
})
export type DeepfakeThreadDetail = z.infer<typeof DeepfakeThreadDetailSchema>

/**
 * GET /v1/deepfake/threads/:id/draft: what the screen draws after a reload. `draft` is the thread's newest Pending
 * draft that has not passed its time (or null), with `unsafe` and its reasons already worked out by the API;
 * "Exchange N of M" is `exchangeCount` of `maxExchanges`; `mode` is who answers the thread now.
 */
export const GetThreadDraftResponseSchema = z.object({
  draft: DeepfakeDraftSchema.nullable(),
  /** Replies already sent on this thread, by the agent or by an admin. */
  exchangeCount: z.number().int(),
  /** The most replies this thread may get: the smaller of the campaign's and the company's ceiling. */
  maxExchanges: z.number().int(),
  mode: z.enum(DEEPFAKE_REPLY_MODES),
})
export type GetThreadDraftResponse = z.infer<typeof GetThreadDraftResponseSchema>

// ── responses: outcome ───────────────────────────────────────────────────────

/** What a caller is told about the training of one compromise. */
export const DeepfakeTrainingOutcomeZ = z.object({
  state: z.string(),
  reason: z.string().nullable(),
  /** The course the person is (or would have been) enrolled in; null when none could be resolved. */
  courseId: z.string().nullable(),
  /** Enrolment attempts made so far for this compromise. */
  attempts: z.number().int().nonnegative(),
})
export type DeepfakeTrainingOutcome = z.infer<typeof DeepfakeTrainingOutcomeZ>

/** POST /v1/deepfake/targets/:id/mark-compromised */
export const MarkCompromisedResponseZ = z.object({
  id: z.string(),
  compromised: z.boolean(),
  /** false: it already was what the request asked for, and nothing changed. */
  changed: z.boolean(),
  status: z.string(),
  training: DeepfakeTrainingOutcomeZ.nullable(),
})
export type MarkCompromisedResponse = z.infer<typeof MarkCompromisedResponseZ>

// ── the policy the screen reads (web-own: a few fields of GET /v1/deepfake/settings) ──

/**
 * What the reply screen needs of the effective policy: whether the agent may answer by itself, and how many
 * answers a thread may get. The settings screen caches the whole policy under another key.
 */
export const DeepfakeReplyPolicySchema = z.object({
  allowAutoReply: z.boolean(),
  maxExchangesPerPerson: z.number().int(),
  agentDelayMinMinutes: z.number(),
  agentDelayMaxMinutes: z.number(),
})
export type DeepfakeReplyPolicy = z.infer<typeof DeepfakeReplyPolicySchema>
